'use client';

import React, {
  createContext,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import aa from 'search-insights';

import { IdleTrackingForNotRemember } from '@/components/V2/Auth/IdleTracking';
import { COUPON_ADMINS } from '@/config/constants';
import usePageLoaded from '@/helpers/hooks/usePageLoaded';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { useZendeskAuth } from '@/hooks/useZendeskAuth';
import { useAppSelector } from '@/store';
import { useGetCustomerQuery } from '@/store/api/customer';
import { useGetAllCustomPagesQuery, useGetCustomerAgainstWebAliasQuery } from '@/store/api/customerAgainstWebAlias';
import { Customer, CustomerAgainstWebAlias, CustomPage } from '@/types/Customer';
import { CustomerType } from '@/types/CustomerType';

interface AuthContextProps {
  isLoggedIn: boolean | null;
  customer?: Customer;
  customPages?: CustomPage[];
  customerAgainstWebAlias?: CustomerAgainstWebAlias;
  isAdmin: boolean;
  isLoading?: boolean;
  updateCustomerPhoneNumber: (phone: string, updatePrimary?: boolean) => void;
}

interface AuthProviderProps {
  children: ReactNode;
  isBackOffice?: boolean;
  isAuthenticated?: boolean;
}

const AuthContext = createContext<AuthContextProps>({
  isLoggedIn: null,
} as AuthContextProps);

const AuthProvider = ({ children, isBackOffice, isAuthenticated }: AuthProviderProps) => {
  const { webalias } = useWebAlias();
  const isPageLoaded = usePageLoaded();
  const [customer, setCustomer] = useState<Customer>({
    firstName: '',
    lastName: '',
    customerID: undefined,
    email: undefined,
  } as unknown as Customer);
  const isAuthenticatedInStore = !!useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);
  const customerRef = useRef<Customer>();

  // Optimization Changes Flag - Delay IdleTracking mount by 5 seconds
  const [shouldMountIdleTracking, setShouldMountIdleTracking] = useState(false);

  const isUserAuthenticated = isAuthenticated || isAuthenticatedInStore;
  // const { isUserAuthenticated, status } = useCheckIfUserAuthenticatedQuery(undefined, {
  //   selectFromResult: ({ data, status }) => ({
  //     isUserAuthenticated: data?.success,
  //     status,
  //   }),

  //   skip: !isPageLoaded,
  // });

  const { isLoadingCustomer, customer: _customer } = useGetCustomerQuery(undefined, {
    selectFromResult: ({ data, isFetching, isLoading, isUninitialized, status }) => ({
      customer: { ...(data?.data || {}), primaryPhone: data?.data?.primaryNumber } as Customer,
      isLoadingCustomer: isFetching || isLoading,
      isUninitialized,
    }),
    skip: !isUserAuthenticated,
  });

  useEffect(() => {
    if (customer?.customerID) aa('setAuthenticatedUserToken', customer?.customerID);
  }, [customer]);

  // Optimization Changes Flag - Delay IdleTracking mount
  useEffect(() => {
    if (!isUserAuthenticated) {
      setShouldMountIdleTracking(false);
      return;
    }
    const timer = setTimeout(() => {
      setShouldMountIdleTracking(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isUserAuthenticated]);

  const { isLoadingCustomerAgainstWebAlias, customerAgainstWebAlias } = useGetCustomerAgainstWebAliasQuery(
    webalias || 'pawtree',
    {
      selectFromResult: ({ data, isFetching, isLoading }) => ({
        customerAgainstWebAlias: data?.data,
        isLoadingCustomerAgainstWebAlias: isFetching || isLoading,
      }),
      skip: !isPageLoaded || isBackOffice,
    }
  );

  useEffect(() => {
    function updateCustomerInState() {
      if (!_customer || _customer?.customerID == customerRef.current?.customerID)
        return console.warn(
          'not updating customer in state as either customer is undefined or ID is the same',
          'customer object:',
          _customer
        );

      setCustomer(_customer);
    }

    updateCustomerInState();
    customerRef.current = _customer;
  }, [_customer]);

  const customerID = isBackOffice ? customer?.customerID ?? '' : customerAgainstWebAlias?.customerID ?? '';

  const { customPages } = useGetAllCustomPagesQuery(customerID, {
    selectFromResult: ({ data }) => ({
      customPages: data?.data,
    }),
    skip: !isPageLoaded || !customerID,
  });

  const isLoggedIn = !!isUserAuthenticated;

  const isLoading = isLoadingCustomer || isLoadingCustomerAgainstWebAlias;
  const isAdmin = COUPON_ADMINS.includes((customer?.customerID ?? '')?.toString());

  useZendeskAuth({ isAuthenticated: isLoggedIn });

  const updateCustomerPhoneNumber = useCallback((phone: string, updatePrimary?: boolean) => {
    if (!phone.length) return console.warn('no customer to set phone number');
    setCustomer(customer =>
      customer
        ? { ...customer, mobilePhone: phone, ...(updatePrimary ? { primaryNumber: phone, primaryPhone: phone } : {}) }
        : customer
    );
  }, []);

  const value = useMemo(() => {
    return {
      isAdmin,
      customer,
      customerAgainstWebAlias,
      customPages,
      isLoggedIn,
      isLoading,
      updateCustomerPhoneNumber,
    };
  }, [customer, customPages, isLoading, isLoggedIn, customerAgainstWebAlias, isAdmin, updateCustomerPhoneNumber]);

  const isPetPro = customer?.customerTypeID === CustomerType.PET_PRO;

  console.log(
    '🏢 [AuthProvider] Customer:',
    customer?.customerID,
    'CustomerType:',
    customer?.customerTypeID,
    'isPetPro:',
    isPetPro
  );
  console.log('🔐 [AuthProvider] isLoggedIn:', isLoggedIn, 'isUserAuthenticated:', isUserAuthenticated);

  return (
    <AuthContext.Provider value={value}>
      {isLoggedIn && shouldMountIdleTracking && <IdleTrackingForNotRemember isPetPro={isPetPro} />}
      <Suspense fallback={<>loading</>}>{children}</Suspense>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
export default AuthProvider;
